// Dok 60: mini-AMP för små projekt. Fem korta steg som skriver till samma
// underlag som den fullständiga kedjan (AMP-avsnitt + arbetsområdesutredning).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth";
import {
  emptyMiniAmp,
  loadMiniAmp,
  saveMiniAmp,
  type MiniAmpData,
  type MiniAmpOtherRisk,
} from "@/lib/basProcess/miniAmp";

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

const STEP_TITLES = [
  "Projektet",
  "Roller",
  "Arbetsområdet",
  "Särskilda risker",
  "Regler och kontakter",
];

const STEP_HELP = [
  "Vad ska göras, var och av vem. Detta blir avsnittet Orientering i arbetsmiljöplanen.",
  "Vem är byggherre och vilka är utsedda som Bas-P och Bas-U.",
  "Var arbetet sker, hur området är avgränsat och om verksamhet pågår intill.",
  "Gå igenom de 13 särskilda riskerna. Kryssa i de som förekommer och skriv hur ni hanterar dem.",
  "Kortfattade ordnings- och skyddsregler samt vem man ringer om något händer.",
];

const MiniAmpWizard: React.FC<Props> = ({ projectId, open, onOpenChange, onSaved }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<MiniAmpData>(emptyMiniAmp());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await loadMiniAmp(projectId));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      setStep(0);
      void load();
    }
  }, [open, load]);

  const set = <K extends keyof MiniAmpData>(key: K, value: MiniAmpData[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  const setRisk = (id: string, patch: { applicable?: boolean | null; measures?: string }) =>
    setData((d) => ({
      ...d,
      risks: d.risks.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));

  const setOther = (index: number, patch: Partial<MiniAmpOtherRisk>) =>
    setData((d) => ({
      ...d,
      otherRisks: d.otherRisks.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await saveMiniAmp(projectId, data, user?.id ?? null);
      toast.success("Sparat i arbetsmiljöplanen");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dok 60 · Arbetsmiljöplan liten omfattning</DialogTitle>
          <DialogDescription>
            Steg {step + 1} av {STEP_TITLES.length}: {STEP_TITLES[step]} — {STEP_HELP[step]}
          </DialogDescription>
        </DialogHeader>

        <Progress value={((step + 1) / STEP_TITLES.length) * 100} className="h-2" />

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Hämtar underlag …</p>
        ) : (
          <div className="space-y-4 py-2">
            {step === 0 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mini-name">Projektnamn</Label>
                  <Input
                    id="mini-name"
                    value={data.projectName}
                    onChange={(e) => set("projectName", e.target.value)}
                    placeholder="T.ex. Fasadrenovering Storgatan 4"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-address">Adress</Label>
                  <Input
                    id="mini-address"
                    value={data.address}
                    onChange={(e) => set("address", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-desc">Vad ska göras?</Label>
                  <Textarea
                    id="mini-desc"
                    rows={4}
                    value={data.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="Beskriv arbetet i klartext, ungefär som du skulle berätta det för en ny på plats."
                  />
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mini-client">Byggherre</Label>
                  <Input
                    id="mini-client"
                    value={data.client}
                    onChange={(e) => set("client", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-basp">Bas-P (planering och projektering)</Label>
                  <Input
                    id="mini-basp"
                    value={data.basP}
                    onChange={(e) => set("basP", e.target.value)}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="mini-basu">Bas-U (utförande)</Label>
                    <Input
                      id="mini-basu"
                      value={data.basU}
                      onChange={(e) => set("basU", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mini-basu-phone">Telefon Bas-U</Label>
                    <Input
                      id="mini-basu-phone"
                      value={data.basUPhone}
                      onChange={(e) => set("basUPhone", e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mini-area">Arbetsområde</Label>
                  <Input
                    id="mini-area"
                    value={data.areaName}
                    onChange={(e) => set("areaName", e.target.value)}
                    placeholder="T.ex. Gårdssidan, plan 1–3"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-delim">Avgränsning mot omgivningen</Label>
                  <Textarea
                    id="mini-delim"
                    rows={3}
                    value={data.delimitation}
                    onChange={(e) => set("delimitation", e.target.value)}
                    placeholder="Staket, avspärrningar, skyltning, gångvägar för boende."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-adjacent">Pågår verksamhet intill?</Label>
                  <Textarea
                    id="mini-adjacent"
                    rows={3}
                    value={data.adjacentActivity}
                    onChange={(e) => set("adjacentActivity", e.target.value)}
                    placeholder="Lämna tomt om ingen verksamhet berörs."
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <div className="space-y-3">
                {data.risks.map((risk) => (
                  <div key={risk.id} className="rounded-md border p-3">
                    <p className="text-sm leading-snug">{risk.text}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={risk.applicable === true ? "default" : "outline"}
                        onClick={() => setRisk(risk.id, { applicable: true })}
                      >
                        Förekommer
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={risk.applicable === false ? "secondary" : "outline"}
                        onClick={() => setRisk(risk.id, { applicable: false, measures: "" })}
                      >
                        Förekommer inte
                      </Button>
                    </div>
                    {risk.applicable === true && (
                      <Textarea
                        className="mt-2"
                        rows={2}
                        value={risk.measures || ""}
                        onChange={(e) => setRisk(risk.id, { measures: e.target.value })}
                        placeholder="Hur hanterar ni risken? Vem ansvarar?"
                      />
                    )}
                  </div>
                ))}

                <div className="space-y-2 pt-2">
                  <Label>Övriga risker i projektet</Label>
                  {data.otherRisks.map((row, i) => (
                    <div key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
                      <Input
                        value={row.risk}
                        onChange={(e) => setOther(i, { risk: e.target.value })}
                        placeholder="Risk"
                      />
                      <Input
                        value={row.measure}
                        onChange={(e) => setOther(i, { measure: e.target.value })}
                        placeholder="Åtgärd"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={row.responsible}
                          onChange={(e) => setOther(i, { responsible: e.target.value })}
                          placeholder="Ansvarig"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Ta bort risk"
                          onClick={() =>
                            set(
                              "otherRisks",
                              data.otherRisks.filter((_, idx) => idx !== i)
                            )
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      set("otherRisks", [...data.otherRisks, { risk: "", measure: "", responsible: "" }])
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" /> Lägg till risk
                  </Button>
                </div>
              </div>
            )}

            {step === 4 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mini-rules">Ordnings- och skyddsregler</Label>
                  <Textarea
                    id="mini-rules"
                    rows={6}
                    value={data.rules}
                    onChange={(e) => set("rules", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mini-emergency">Vid olycka eller tillbud</Label>
                  <Textarea
                    id="mini-emergency"
                    rows={4}
                    value={data.emergency}
                    onChange={(e) => set("emergency", e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Tillbaka
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={save} disabled={saving || loading}>
              Spara
            </Button>
            {step < STEP_TITLES.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={loading}>
                Nästa <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={save} disabled={saving || loading}>
                <Check className="mr-1 h-4 w-4" /> Spara och stäng
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MiniAmpWizard;
